import { RawParams, StateOrName, TransitionOptions, TransitionService, UIRouterGlobals } from "@uirouter/angular";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Observable, combineLatest, forkJoin, take, takeUntil } from "rxjs";
import { ICollection } from "@webapp/core/core.models";
import { fromTransitionHook } from "@webapp/core/routing/rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { INavItem, NavItemsSectionBroadcastConfig } from "@webapp/navigation/models/nav-items-list.models";
import { FAVORITES_STEP, NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS, RECENTS_AND_SUGGESTED_STEP, REPORTS_STEP } from "@webapp/navigation/models/navigation-onboarding.models";
import { SubNavOption } from "@webapp/navigation/models/sub-nav-options.models";
import { NavigationOnboardingService } from "@webapp/navigation/services/navigation-onboarding.service";
import NavigationItemsMediator from "@webapp/navigation/services/uxcustomization/navigation-items.mediator.service";
import { SuggestedFacade } from "@webapp/navigation/services/uxcustomization/suggested/suggested.facade.service";
import { SessionsSubNavigationOptions } from "@webapp/navigation/utils/sessions-sub-navigation-options";
import { IOnboardingDefinition, IOnboardingDelegate, IOnboardingJourneyState } from "@webapp/shared/components/onboarding/onboarding.models";
import { BaseSubNav } from "../services/base-sub-nav";
import { NavItemsSectionBroadcastConfigFactory } from "../services/nav-items-section-broadcast-config.factory";

@Component({
  selector: "sessions-sub-navigation",
  templateUrl: "./sessions-sub-navigation.component.html",
})
export class SessionsSubNavigationComponent extends BaseSubNav implements OnInit, IOnboardingDelegate {
  public options: SubNavOption[] = [];
  public suggested$: Observable<ICollection<INavItem>>;
  public onboarding: IOnboardingDefinition = { shouldShow: false, totalSteps: 0, previewOverrides: { totalSteps: NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS } };
  public broadcastConfig: NavItemsSectionBroadcastConfig = this.broadcastConfigFactory.buildDefaultNavItemsSectionBroadcastConfig();

  public constructor(
    private cdr: ChangeDetectorRef,
    private suggestedFacade: SuggestedFacade,
    protected navigationItemsMediator: NavigationItemsMediator,
    private transitionService: TransitionService,
    private routerGlobals: UIRouterGlobals,
    private navigationOnboardingService: NavigationOnboardingService,
    private destroyService$: DestroyService,
    private featureTogglesFacade: FeatureTogglesFacade,
    private broadcastConfigFactory: NavItemsSectionBroadcastConfigFactory,
    private sessionsSubNavigationOptions: SessionsSubNavigationOptions
  ) {
    super();
    this.setupEntityTypes(["session", "filter"], "sessions");
    this.suggested$ = this.suggestedFacade.getSuggestedSessions$();

    this.navigationOnboardingService
      .getState$()
      .pipe(takeUntil(this.destroyService$))
      .subscribe((state: IOnboardingJourneyState) => {
        this.onboarding = { shouldShow: false, totalSteps: 0, delegate: undefined, previewOverrides: { totalSteps: NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS } };
        if (state.stage === "ONGOING" && state.currentStep === RECENTS_AND_SUGGESTED_STEP) {
          this.onboarding.startingStep = RECENTS_AND_SUGGESTED_STEP;
          this.onboarding.totalSteps = NEW_NAVIGATION_ONBOARDING_TOTAL_STEPS;
          this.onboarding.shouldShow = true;
          this.onboarding.delegate = this;
          this.onboarding.previewOverrides.totalSteps = state.totalSteps;
        }
        if (state.stage === "ENDED_BY_USER") {
          this.onboarding.shouldShow = false;
        }
      });
  }

  public shouldSeeOnboarding(): boolean {
    return this.onboarding.shouldShow;
  }

  public getRedirectStateForStep(step: number): { to: StateOrName; params?: RawParams; options?: TransitionOptions } {
    if (step === FAVORITES_STEP) {
      this.navigationOnboardingService.moveBackward({ inTransition: true });
      return { to: "gtmhub.home" };
    }
    if (step === REPORTS_STEP) {
      this.navigationOnboardingService.moveForward({ inTransition: true });
      return { to: "gtmhub.lists" };
    }
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.setSubNavigationOptions();
    this.setBroadcastConfigBasedOnFeatureFlag();
  }

  protected itemMarkedAsFavorite(item: INavItem): void {
    this.suggestedFacade.removeSuggestedItem(item.id, "sessions");
  }

  private setSubNavigationOptions(): void {
    forkJoin([this.sessionsSubNavigationOptions.allSessions$(), this.sessionsSubNavigationOptions.okrViews$(), this.sessionsSubNavigationOptions.okrsForApproval$()])
      .pipe(take(1))
      .subscribe((options) => {
        this.options = options.filter((option) => !!option);
        this.cdr.detectChanges();
      });
  }

  private setBroadcastConfigBasedOnFeatureFlag(): void {
    combineLatest([
      this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.ShareableLinksWithAppliedFilters),
      this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.OkrViews),
    ])
      .pipe(takeUntil(this.destroyService$))
      .subscribe(([isShareableLinksWithAppliedFiltersFeatureEnabled, isOkrViewsFeatureEnabled]) => {
        this.setBroadcastConfig({ isShareableLinksWithAppliedFiltersFeatureEnabled, isOkrViewsFeatureEnabled });

        fromTransitionHook(this.transitionService, "onSuccess", {})
          .pipe(takeUntil(this.destroyService$))
          .subscribe(() => {
            this.setBroadcastConfig({ isShareableLinksWithAppliedFiltersFeatureEnabled, isOkrViewsFeatureEnabled });
          });
      });
  }

  private setBroadcastConfig(params: { isShareableLinksWithAppliedFiltersFeatureEnabled: boolean; isOkrViewsFeatureEnabled: boolean }): void {
    this.broadcastConfig = this.broadcastConfigFactory.buildSessionNavItemsSectionBroadcastConfig({
      isShareableLinksWithAppliedFiltersFeatureEnabled: params.isShareableLinksWithAppliedFiltersFeatureEnabled,
      isOkrViewsFeatureEnabled: params.isOkrViewsFeatureEnabled,
      currentStateName: this.routerGlobals.current.name,
    });
  }
}
